"use client"

import React, { useEffect, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import { MappingsPage } from "@/components/mappings/mappings-page"
import { ExplorerPage } from "@/components/explorer/explorer-page"
import { TargetMappingEditor } from "@/components/target-mapping/target-mapping-editor"
import { ValueMappingSetsPage } from "@/components/value-mapping-sets/value-mapping-sets-page"
import { AtsSourcesPage } from "@/components/ats-sources/ats-sources-page"
import { EventSyncPage } from "@/components/event-sync/event-sync-page"
import { AtsSourceBar } from "@/components/ats-source-bar"
import { HubShell } from "@/components/hub/hub-shell"
import { useCode } from "@/contexts/code-context"
import { useAtsSource } from "@/contexts/ats-source-context"
import { useTranslation } from "@/hooks/use-translation"
import { invalidateReferenceDataCache } from "@/hooks/use-explorer"
import { clearSchemaCache } from "@/lib/ats-schema-cache"
import type { TranslationKey } from "@/lib/translations"
import { Loader2 } from "lucide-react"

/** The tools, in the order the tabs present them; `null` is the Mappings view */
type ToolView = string | null

export default function FieldMappingPage() {
  const searchParams = useSearchParams()
  const mappingName = searchParams.get("mappingName")
  const view = searchParams.get("view")
  const { t } = useTranslation()
  const { isLoading: codeValuesLoading, error: codeValuesError } = useCode()
  const { hasNoSources, selectedSource } = useAtsSource()

  // A tool is mounted the first time it is opened and stays mounted, so moving between
  // tabs keeps each one's filters, search and selection. Refresh starts them over.
  const [generation, setGeneration] = useState(0)
  const [visited, setVisited] = useState<ToolView[]>([view])
  useEffect(() => {
    setVisited((current) => (current.includes(view) ? current : [...current, view]))
  }, [view])

  // The mapping under edit is in the URL, which the other tabs drop, and the Mappings tab
  // pushes a "selector" sentinel rather than a name. The tool stays mounted, so it is handed
  // the last real mapping it was given and the editor survives a trip to another tab.
  const lastMappingName = useRef<string | null>(mappingName)
  if (view === null && mappingName !== null && mappingName !== "selector") {
    lastMappingName.current = mappingName
  }

  // The explorer is keyed by source because its state is all source-scoped, so switching
  // source starts it afresh. A momentary null is not a switch — the source list reloading
  // would otherwise remount the explorer empty and lose the user's place — so the last real
  // code is kept until a different one arrives.
  const lastSourceCode = useRef<string | null>(null)
  if (selectedSource?.code) lastSourceCode.current = selectedSource.code

  const refresh = () => {
    invalidateReferenceDataCache()
    clearSchemaCache()
    setVisited([view])
    setGeneration((current) => current + 1)
  }

  // Don't render until code values are loaded
  if (codeValuesLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2">{t("common.state.loading")}</p>
        </div>
      </div>
    )
  }

  if (codeValuesError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">{t("page.errors.loadFailed")}</p>
          <p className="mt-2 text-muted-foreground">{codeValuesError}</p>
        </div>
      </div>
    )
  }

  // Sources and Event Sync manage source/sync config themselves — not scoped by the switcher
  const sourceScoped = view !== "ats-sources" && view !== "event-sync"
  // The Mappings tool (view === null) renders its own no-source state inside the rail layout
  const blockedWithoutSource = sourceScoped && hasNoSources && view !== null

  const toolFor = (tool: ToolView) => {
    if (tool === "ats-sources") return <AtsSourcesPage />
    if (tool === "event-sync") return <EventSyncPage />
    // The pull blade opens over the explorer (see HubShell)
    if (tool === "explorer" || tool === "data-pull") return <ExplorerPage key={lastSourceCode.current ?? "no-source"} />
    if (tool === "target-mapping") return <TargetMappingEditor />
    if (tool === "value-mapping-sets") return <ValueMappingSetsPage />
    // Mappings: rail of mapping types + the selected editor
    return <MappingsPage mappingName={lastMappingName.current} />
  }

  // Explorer and data-pull are the same tool, so one pane serves both
  const paneOf = (tool: ToolView) => (tool === "data-pull" ? "explorer" : tool)
  const activePane = paneOf(view)
  const panes = Array.from(new Set(visited.map(paneOf)))

  // The app owns the iframe viewport (h-screen, no body scroll): the source bar and the
  // hub header/tabs stay pinned, only the tool canvas scrolls (the explorer fills it).
  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {sourceScoped && <AtsSourceBar />}
      <div className="flex min-h-0 flex-1 flex-col">
        <HubShell view={view} mappingName={mappingName} hasSources={!hasNoSources} onRefresh={refresh}>
          {blockedWithoutSource ? (
            <div className="container mx-auto py-12 text-center text-muted-foreground">
              {t("page.emptyState.noSources")}
            </div>
          ) : (
            panes.map((pane) => (
              <div key={`${generation}:${pane ?? "mappings"}`} className={pane === activePane ? "flex min-h-0 flex-1 flex-col" : "hidden"}>
                {toolFor(pane)}
              </div>
            ))
          )}
        </HubShell>
      </div>
    </div>
  )
}
